import type { FlockEntry } from "./DailyReportPage";

interface Props {
  flocks: FlockEntry[];
  onChange: (index: number, field: keyof FlockEntry, value: string) => void;
  /** True when this date already had measurements saved, so the boxes below
   *  are showing what is on record rather than a blank sheet. */
  prefilled: boolean;
  /** True when the day's existing readings could NOT be loaded. The boxes are
   *  then blank for a reason that has nothing to do with the farm, and the
   *  step must not let anything be written over what it could not read. */
  loadFailed: boolean;
}

const inputClass =
  "w-full rounded-lg border border-gray-300 bg-transparent px-3 py-2 text-sm text-gray-800 shadow-theme-xs focus:border-brand-500 focus:outline-none focus:ring focus:ring-brand-500/10 dark:border-gray-700 dark:bg-gray-900 dark:text-white/90";

/**
 * The fields FIELDS is allowed to name: the `m_`-prefixed entries of
 * FlockEntry, minus `m_notes`.
 *
 * Both exclusions are load-bearing, because `hasAnyMeasure` below decides
 * whether a flock's row is SENT AT ALL. A plain `keyof FlockEntry` would admit
 * `mortality`, which defaults to "0" — every flock would then count as
 * measured, and the blank-flock protection that stops an empty row erasing a
 * day would be off. `m_notes` is carried through form state but never shown,
 * and a note alone must not make a flock "measured" either (the server agrees:
 * its HasAnyMeasure ignores notes).
 */
type MeasureField = Exclude<Extract<keyof FlockEntry, `m_${string}`>, "m_notes">;

// The seven measures, as they appear on the form. `layerOnly` hides egg weight
// for a brooder shed — the same rule StepFlockData uses to hide the egg boxes.
// NOTE that hiding is not clearing: the submit path still sends the prefilled
// egg weight for a brooder flock, because a value the operator was never shown
// is not a value they asked to delete.
const FIELDS: {
  field: MeasureField;
  label: string;
  unit: string;
  step: string;
  min?: string;
  placeholder: string;
  layerOnly?: boolean;
}[] = [
  { field: "m_body_weight_g", label: "Body weight", unit: "g/bird", step: "1", min: "0", placeholder: "e.g. 1450" },
  { field: "m_egg_weight_g", label: "Egg weight", unit: "g/egg", step: "0.1", min: "0", placeholder: "e.g. 58", layerOnly: true },
  { field: "m_water_litres", label: "Water", unit: "L", step: "1", min: "0", placeholder: "e.g. 900" },
  { field: "m_light_hours", label: "Light", unit: "hours", step: "0.5", min: "0", placeholder: "e.g. 16" },
  { field: "m_temp_min_c", label: "Temp low", unit: "°C", step: "0.1", placeholder: "e.g. 22" },
  { field: "m_temp_max_c", label: "Temp high", unit: "°C", step: "0.1", placeholder: "e.g. 34" },
  { field: "m_humidity_pct", label: "Humidity", unit: "%", step: "1", min: "0", placeholder: "e.g. 65" },
];

/** Whether this flock's card carries any reading at all. Drives the "saved"
 *  badge and mirrors the rule the submit path uses to decide what to send. */
export function hasAnyMeasure(f: FlockEntry): boolean {
  return FIELDS.some((d) => f[d.field].trim() !== "");
}

export default function StepMeasurements({
  flocks,
  onChange,
  prefilled,
  loadFailed,
}: Props) {
  const filledCount = flocks.filter(hasAnyMeasure).length;

  return (
    <div>
      <h4 className="mb-1 text-base font-semibold text-gray-800 dark:text-white/90">
        Measurements
      </h4>
      <p className="mb-4 text-xs text-gray-500 dark:text-gray-400">
        Optional — leave a box blank if it wasn't measured. Most farms weigh a
        sample once a week; the shed readings are daily. A blank box is
        <span className="font-medium"> not measured</span>, which is not the same
        as zero.
      </p>

      {loadFailed && (
        <div className="mb-4 rounded-lg bg-warning-50 px-3 py-2 text-xs text-warning-700 dark:bg-warning-500/15 dark:text-warning-400">
          <span className="font-medium">
            Couldn't load this date's existing measurements.
          </span>{" "}
          The boxes below are blank because the reading failed, not because
          nothing was measured — so saving here is switched off rather than
          risk erasing what's already stored. Go back to the Date step and
          re-pick the date to try again. Everything else on this report will
          still submit normally.
        </div>
      )}

      {prefilled && !loadFailed && (
        <div className="mb-4 rounded-lg bg-brand-50 px-3 py-2 text-xs text-brand-700 dark:bg-brand-500/15 dark:text-brand-400">
          Showing what's already saved for this date. Change a box to correct it;
          clear a box to remove that one reading.
        </div>
      )}

      <div className="space-y-5">
        {flocks.map((f, i) => (
          <div
            key={f.flock_id}
            className="rounded-xl border border-gray-200 p-4 dark:border-gray-700"
          >
            <div className="mb-3 flex items-center justify-between">
              <h5 className="text-sm font-semibold text-gray-800 dark:text-white/90">
                {f.shed_code || f.flock_type}
              </h5>
              <div className="flex items-center gap-2">
                <span className="text-xs text-gray-500 dark:text-gray-400">
                  {f.batch_name}
                </span>
                <span
                  className={`rounded-full px-2 py-0.5 text-[10px] font-medium ${
                    f.shed_type === "layer"
                      ? "bg-brand-50 text-brand-600 dark:bg-brand-500/15 dark:text-brand-400"
                      : "bg-warning-50 text-warning-600 dark:bg-warning-500/15 dark:text-warning-400"
                  }`}
                >
                  {f.shed_type}
                </span>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              {FIELDS.filter((d) => !d.layerOnly || f.shed_type === "layer").map((d) => (
                <div key={d.field}>
                  <label className="mb-1 block text-xs font-medium text-gray-600 dark:text-gray-400">
                    {d.label}{" "}
                    <span className="font-normal text-gray-400">({d.unit})</span>
                  </label>
                  <input
                    type="number"
                    min={d.min}
                    step={d.step}
                    value={f[d.field]}
                    onChange={(e) => onChange(i, d.field, e.target.value)}
                    disabled={loadFailed}
                    className={`${inputClass} disabled:cursor-not-allowed disabled:opacity-50`}
                    placeholder={d.placeholder}
                  />
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>

      <p className="mt-4 text-xs text-gray-500 dark:text-gray-400">
        {loadFailed
          ? "Measurements won't be saved for this report."
          : filledCount === 0
            ? "Nothing measured — this step will be skipped."
            : `${filledCount} of ${flocks.length} ${flocks.length === 1 ? "flock" : "flocks"} measured.`}
      </p>
    </div>
  );
}
